/**
 * Town layout, shared by both renderers (night-town.ts draws it on a 2D canvas, diorama.ts builds a WebGL scene
 * from it). Every recorded moment is an island block moored in a lagoon, and every word, expression, heard line
 * or suggested reply is a lot on it. Islands are placed once, in recording order, beside the earlier moment
 * they share most words with, so a growing town only ever adds islands. Two moments that share words get a
 * road: a bridge across the canal when their islands are neighbours, a string of lanterns otherwise. In v1
 * roads are layout only, so every bridge is rope; the prototype passes its own bridge materials.
 * No DOM here: the unit tests run this under Node.
 */
import { LANTERN_TOP, propsSpot, type PropsSpot } from './props';
import { districtHue, hash, rng, type District, type Selection, type Tile, type Town } from './town-data';

/** What a bridge is built of: 0 rope, 1 wood, 2 stone. v1 builds every bridge of rope. */
export type Material = 0 | 1 | 2;
/** Picks a bridge's material from the words its two moments share (tiles), and the two moments' ids. */
export type MaterialOf = (tiles: Tile[], a: string, b: string) => Material;

/** Screen rectangle in world units (x0, y0, x1, y1) that is on screen, with a margin. */
export type Box = [number, number, number, number];

/**
 * Things afloat that B draws with the town (the prototype's ferries): a pass on the water, then each item in
 * the town's back-to-front pass, by its painter's depth. Items must be sorted by depth.
 */
export interface Afloat {
  items: readonly { depth: number }[];
  water(c: CanvasRenderingContext2D, sel: Selection, u: number, view: Box): void;
  draw(c: CanvasRenderingContext2D, index: number, lod: number, u: number, view: Box): void;
}

// ---- geometry -----------------------------------------------------------------------------------------

/** Half a lot's screen width and height at zoom 1 (2:1 isometric). World z is in the same units as screen y. */
export const HW = 16;
export const HH = 8;
/** Water sits a little below the quays; the diorama's earth sides go down to -SLAB. */
export const SEA = -3;
export const SLAB = 14;
export const cid = (x: number, y: number) => (x + 4096) * 8192 + (y + 4096);
/** Lot coordinates (x right-down, y left-down, z up) → world screen coordinates at zoom 1. */
export const sx = (x: number, y: number) => (x - y) * HW;
export const sy = (x: number, y: number, z = 0) => (x + y) * HH - z;

// ---- layout: each island is placed once, when its moment arrives --------------------------------------

export interface Block {
  d: District;
  hue: number;
  x: number; y: number; w: number; h: number;
  /** Land cells in lot order (back to front): tile i of the district always sits on cells[i]. */
  cells: [number, number][];
  /** Which way the island's row houses run (0 along x, 1 along y). */
  axis: 0 | 1;
  /** Land cells whose front-left (+y) / front-right (+x) neighbour is water: they show a quay wall. */
  quayL: [number, number][];
  quayR: [number, number][];
}

export interface Lot {
  t: Tile;
  b: Block;
  gx: number; gy: number;
  /** Deterministic variety numbers (0..1) for this word. */
  r: number[];
  seed: number;
  /** Highest point of what stands on the lot (world units), for hit-testing, flags and the selection marker. */
  top: number;
  mist: string;
  /** Flag colour when the word is a bridge word (the other moment's hue). */
  flag: string | null;
}

export interface Bridge {
  a: Block; b: Block;
  axis: 0 | 1;
  /** Row (or column) the bridge runs along, and the first/last canal cell it spans. */
  row: number; from: number; to: number;
  shared: string[];
  width: number;
  /** Best stage among the shared words (the diorama builds with it). */
  stage: number;
  /** What it is built of: always rope in v1 (roads are layout only); the prototype picks rope, wood or stone. */
  material: Material;
  lit: boolean;
}

/**
 * The empty land (v1 ticket 40): a town with no district yet is one plot of bare land in the lagoon, where the first
 * district will stand. Not a block: it has no lots, no district and nothing to tap.
 */
export interface Plot {
  cells: [number, number][];
  /** Its cells whose front-left (+y) / front-right (+x) neighbour is water: they show a quay wall. */
  quayL: [number, number][];
  quayR: [number, number][];
  /** The ground's tint, as a district's hue tints its island. */
  hue: number;
}

/** The empty land's cells: 3 by 3 round the middle, about where a first moment's island will stand. */
const PLOT = 1;
/** The empty land's tint. */
export const PLOT_HUE = 36;

/** Two moments that share words but are not canal neighbours: a string of lanterns in the air. */
export interface Garland { from: Lot | null; a: Block; b: Block; shared: string[]; lit: boolean }

type Item =
  | { depth: number; k: 0; lot: Lot }
  | { depth: number; k: 1; bridge: Bridge }
  | { depth: number; k: 2; b: Block; x: number; y: number }
  | { depth: number; k: 3; props: PropsSpot };

export interface Layout {
  blocks: Block[];
  byDistrict: Map<string, Block>;
  lots: Lot[];
  byKey: Map<string, Lot>;
  land: Map<number, Block>;
  water: [number, number][];
  /** Diorama edge faces: cell x, y and the surface z they hang from. */
  sideL: [number, number, number][];
  sideR: [number, number, number][];
  items: Item[];
  bridges: Bridge[];
  garlands: Garland[];
  /** The empty land, while the town has no district (v1 ticket 40). */
  plot?: Plot;
  /**
   * Where the signpost, its lantern and the founding stone stand (v1 ticket 40), when the town has props: the middle
   * of the empty land, else a corner of the oldest district, in the water off its land, clear of every bridge.
   */
  props?: PropsSpot;
  /** B only, added after layout by the prototype: ferries between moments with kin words recalled on both sides. */
  afloat?: Afloat;
  /** B only, added after layout: bridges mostly hidden behind taller lots in front, redrawn faintly on top. */
  xray?: Bridge[];
  /** World screen x, y (and radius for glows). */
  glows: [number, number, number][];
  reflections: [number, number][];
  ripples: [number, number, number][];
  bounds: { x0: number; y0: number; x1: number; y1: number };
  stars: number;
  moon: number;
  seed: string;
}

/**
 * Island footprint for a moment: a near-square block of lots, with the spare cells (w·h − n) cut from the
 * corners into the water so shores are a little irregular. Depends only on the district's own tiles.
 */
function shape(d: District): { w: number; h: number; cells: [number, number][] } {
  const n = Math.max(1, d.tiles.length);
  const r = rng(`tb-shape|${d.id}`);
  const w = Math.max(1, Math.round(Math.sqrt(n * (0.75 + r() * 0.6))));
  const h = Math.ceil(n / w);
  const all: [number, number, number][] = [];
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const dx = (i + 0.5) / w - 0.5, dy = (j + 0.5) / h - 0.5;
    all.push([i, j, dx * dx + dy * dy + r() * 0.02]);
  }
  const cut = new Set([...all].sort((p, q) => q[2] - p[2]).slice(0, w * h - n).map(([i, j]) => i * 1000 + j));
  const cells = all
    .filter(([i, j]) => !cut.has(i * 1000 + j))
    .map(([i, j]) => [i, j] as [number, number])
    .sort((p, q) => p[0] + p[1] - (q[0] + q[1]) || p[0] - q[0]);
  return { w, h, cells };
}

/**
 * Placement weights. `wide` stretches the town along the screen's vertical (iso x+y), so a big town fills a
 * portrait phone; `touch` rewards mooring right across the canal from a moment that shares words (so the
 * shared word can become a real bridge); `margin` is the lagoon's width around the outer islands.
 */
const TUNE = { compact: 2, wide: 3.5, pull: 0.3, touch: 8, margin: 1 };

/**
 * Where a new island goes: every spot that touches an earlier island across exactly one canal, scored by
 * closeness to the earlier moments it shares words with, then by closeness to the first island.
 * Uses only earlier islands, so it never changes when later moments arrive.
 */
function place(placed: Block[], w: number, h: number, id: string, pull: { b: Block; wt: number }[], ox: number, oy: number): [number, number] {
  let best: [number, number] = [0, 0], bestCost = Infinity;
  const maxWt = Math.max(0, ...pull.map((p) => p.wt));
  const free = (x: number, y: number) =>
    placed.every((p) => x + w + 1 <= p.x || p.x + p.w + 1 <= x || y + h + 1 <= p.y || p.y + p.h + 1 <= y);
  const touches = (x: number, y: number, p: Block) =>
    ((x + w + 1 === p.x || p.x + p.w + 1 === x) && y < p.y + p.h && p.y < y + h) ||
    ((y + h + 1 === p.y || p.y + p.h + 1 === y) && x < p.x + p.w && p.x < x + w);
  const tryAt = (x: number, y: number) => {
    if (!free(x, y)) return;
    const cx = x + w / 2, cy = y + h / 2;
    const e = (cx - ox - (cy - oy)) * Math.SQRT1_2, f = (cx - ox + (cy - oy)) * Math.SQRT1_2;
    let cost = TUNE.compact * Math.hypot(e * TUNE.wide, f) + (hash(`${id}|${x}|${y}`) / 4294967296) * 1.2;
    if (pull.length) {
      let dsum = 0, wsum = 0;
      for (const p of pull) { dsum += p.wt * Math.hypot(cx - (p.b.x + p.b.w / 2), cy - (p.b.y + p.b.h / 2)); wsum += p.wt; }
      cost += (TUNE.pull * dsum) / wsum;
      for (const p of pull) if (touches(x, y, p.b)) cost -= (TUNE.touch * p.wt) / maxWt;
    }
    if (cost < bestCost) { bestCost = cost; best = [x, y]; }
  };
  for (const p of placed) {
    for (let y = p.y - h + 1; y <= p.y + p.h - 1; y++) { tryAt(p.x + p.w + 1, y); tryAt(p.x - 1 - w, y); }
    for (let x = p.x - w + 1; x <= p.x + p.w - 1; x++) { tryAt(x, p.y + p.h + 1); tryAt(x, p.y - 1 - h); }
  }
  return best;
}

/** Height of whatever stands on the lot; mirrors night-town.ts's drawing code. */
function topOf(t: Tile, r: number[]): number {
  const v = Math.round((r[0] - 0.5) * 3), k = t.item.kind;
  switch (t.stage) {
    case 0: return 4;
    case 1: return 3;
    case 2: return k === 'line' ? 12 : k === 'expression' ? 12.5 : k === 'suggestion' ? 13.5 : 15;
    case 3: return k === 'line' ? 14 + v * 0.5 : k === 'expression' ? 19 : k === 'suggestion' ? 13.5 : 17 + v;
    default: return k === 'line' ? 22 + v : k === 'expression' ? 27 : k === 'suggestion' ? 14 : 26 + v;
  }
}

/** Find a straight bridge between two islands across ≤ 4 canal cells, as close as possible to the bridge word. */
function crossing(a: Block, b: Block, anchor: Lot | undefined, land: Map<number, Block>, used: Set<number>) {
  const opts: { axis: 0 | 1; row: number; from: number; to: number; cost: number }[] = [];
  const ax = anchor ? anchor.gx : a.x + a.w / 2, ay = anchor ? anchor.gy : a.y + a.h / 2;
  const along = (axis: 0 | 1, L: Block, R: Block) => {
    const [l0, lw, r0, lo0, lo1, ro0, ro1] = axis === 0
      ? [L.x, L.w, R.x, L.y, L.y + L.h, R.y, R.y + R.h]
      : [L.y, L.h, R.y, L.x, L.x + L.w, R.x, R.x + R.w];
    const gap = r0 - (l0 + lw);
    if (gap < 1 || gap > 4) return;
    for (let row = Math.max(lo0, ro0); row < Math.min(lo1, ro1); row++) {
      const cell = (s: number) => (axis === 0 ? cid(s, row) : cid(row, s));
      if (land.get(cell(l0 + lw - 1)) !== L || land.get(cell(r0)) !== R) continue;
      let clear = true;
      for (let s = l0 + lw; s < r0; s++) if (land.has(cell(s)) || used.has(cell(s))) clear = false;
      if (!clear) continue;
      const mid = (l0 + lw + r0 - 1) / 2;
      const [mx, my] = axis === 0 ? [mid, row] : [row, mid];
      opts.push({ axis, row, from: l0 + lw, to: r0 - 1, cost: Math.hypot(mx - ax, my - ay) + gap * 0.5 });
    }
  };
  along(0, a, b); along(0, b, a); along(1, a, b); along(1, b, a);
  opts.sort((p, q) => p.cost - q.cost || p.row - q.row);
  return opts[0] ?? null;
}

/** The four cells diagonal to a cell, the island's screen-left corner first. */
const DIAGONALS: [number, number][] = [[-1, 1], [1, -1], [1, 1], [-1, -1]];

/**
 * Where the props stand beside a district (v1 ticket 40): a water cell that meets its land at a corner and no
 * land along an edge, off every bridge, so the islet under them touches the district at one corner and nothing
 * else. The screen-left corner goes first, then the front, and a cell with an island right in front of it (whose
 * houses could hide the signpost) last. Failing that, any cell next to its land that is neither land nor a bridge,
 * and then one on the lagoon's far side of its left corner. `toward` points at the land the islet leans on.
 */
function propsCorner(b: Block, land: Map<number, Block>, used: Set<number>): { cell: [number, number]; toward: [number, number] } {
  const free = (x: number, y: number) => !land.has(cid(x, y)) && !used.has(cid(x, y));
  const edgeLand = (x: number, y: number) =>
    land.has(cid(x + 1, y)) || land.has(cid(x - 1, y)) || land.has(cid(x, y + 1)) || land.has(cid(x, y - 1));
  const hidden = (x: number, y: number) => land.has(cid(x + 1, y)) || land.has(cid(x, y + 1)) || land.has(cid(x + 1, y + 1));
  const cost = (x: number, y: number) => (hidden(x, y) ? 1000 : 0) + (x - y) * 10 - (x + y);
  let best = null as { cell: [number, number]; toward: [number, number]; cost: number } | null;
  const consider = (x: number, y: number, toward: [number, number], strict: boolean) => {
    if (!free(x, y) || (strict && edgeLand(x, y))) return;
    const c = cost(x, y);
    if (!best || c < best.cost || (c === best.cost && (x < best.cell[0] || (x === best.cell[0] && y < best.cell[1])))) best = { cell: [x, y], toward, cost: c };
  };
  for (const strict of [true, false]) {
    for (const [x, y] of b.cells) {
      for (const [dx, dy] of DIAGONALS) consider(x + dx, y + dy, [-dx, -dy], strict);
      if (!strict) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) consider(x + dx, y + dy, [-dx, -dy], false);
    }
    if (best) return { cell: best.cell, toward: best.toward };
  }
  // Every cell round the island is land or bridge (it cannot happen with a lagoon between islands): off the corner.
  for (let r = 2; ; r++) if (free(b.x - r, b.y + b.h - 1 + r)) return { cell: [b.x - r, b.y + b.h - 1 + r], toward: [0, 0] };
}

/** Lays out a town. `materialOf` picks each bridge's material; v1 leaves it at rope. */
export function layoutTown(town: Town, materialOf: MaterialOf = () => 0): Layout {
  const districts = [...town.districts].sort((p, q) => p.order - q.order);
  const pulls = new Map<string, { other: string; wt: number }[]>();
  for (const r of town.roads) {
    const wt = r.shared.length * 3 + Math.min(r.related, 6) * 0.5;
    if (!wt) continue;
    for (const [k, o] of [[r.a, r.b], [r.b, r.a]]) {
      const list = pulls.get(k) ?? [];
      list.push({ other: o, wt });
      pulls.set(k, list);
    }
  }

  const blocks: Block[] = [];
  const byDistrict = new Map<string, Block>();
  let ox = 0, oy = 0;
  for (const d of districts) {
    const s = shape(d);
    let x: number, y: number;
    if (!blocks.length) {
      x = -Math.floor(s.w / 2); y = -Math.floor(s.h / 2);
      ox = x + s.w / 2; oy = y + s.h / 2;
    } else {
      const pull = (pulls.get(d.id) ?? [])
        .map((p) => ({ b: byDistrict.get(p.other)!, wt: p.wt }))
        .filter((p) => p.b);
      [x, y] = place(blocks, s.w, s.h, d.id, pull, ox, oy);
    }
    const b: Block = {
      d, hue: districtHue(d), x, y, w: s.w, h: s.h,
      cells: s.cells.map(([i, j]) => [x + i, y + j] as [number, number]),
      axis: hash(`tb-axis|${d.id}`) & 1 ? 1 : 0, quayL: [], quayR: [],
    };
    blocks.push(b);
    byDistrict.set(d.id, b);
  }

  const land = new Map<number, Block>();
  for (const b of blocks) for (const [x, y] of b.cells) land.set(cid(x, y), b);
  for (const b of blocks) for (const [x, y] of b.cells) {
    if (!land.has(cid(x, y + 1))) b.quayL.push([x, y]);
    if (!land.has(cid(x + 1, y))) b.quayR.push([x, y]);
  }

  // Lots: tile i of a district on cell i of its island.
  const lots: Lot[] = [];
  const byKey = new Map<string, Lot>();
  const items: Item[] = [];
  for (const b of blocks) {
    b.d.tiles.forEach((t, i) => {
      const [gx, gy] = b.cells[i];
      const g = rng(`tb|${t.key}`);
      const r = [g(), g(), g(), g(), g(), g()];
      const other = t.bridges.length ? byDistrict.get(t.bridges[0]) : undefined;
      const lot: Lot = {
        t, b, gx, gy, r, seed: hash(t.key), top: topOf(t, r),
        mist: `rgba(150, 172, 235, ${(0.07 + r[4] * 0.05).toFixed(3)})`,
        flag: other ? `hsl(${other.hue}, 80%, 64%)` : null,
      };
      lots.push(lot);
      byKey.set(t.key, lot);
      items.push({ depth: (gx + gy) * 2, k: 0, lot });
    });
    // A moment whose words were all heard before still gets a small plaza, so its bridges can land.
    for (let i = b.d.tiles.length; i < b.cells.length; i++) {
      const [x, y] = b.cells[i];
      items.push({ depth: (x + y) * 2, k: 2, b, x, y });
    }
  }

  // The empty land (v1 ticket 40): no district yet, so one plot of bare land where the first will stand.
  let plot: Plot | undefined;
  if (!blocks.length) {
    const cells: [number, number][] = [];
    for (let y = -PLOT; y <= PLOT; y++) for (let x = -PLOT; x <= PLOT; x++) cells.push([x, y]);
    cells.sort((p, q) => p[0] + p[1] - (q[0] + q[1]) || p[0] - q[0]);
    plot = { cells, quayL: cells.filter(([, y]) => y === PLOT), quayR: cells.filter(([x]) => x === PLOT), hue: PLOT_HUE };
  }
  const ground = new Set<number>(land.keys());
  for (const [x, y] of plot?.cells ?? []) ground.add(cid(x, y));

  // Water: a lagoon two lots wide around every island, holes filled so no sky shows through the town.
  const dio = new Set<number>(ground);
  const water: [number, number][] = [];
  let mx0 = Infinity, my0 = Infinity, mx1 = -Infinity, my1 = -Infinity;
  const addWater = (x: number, y: number) => {
    const k = cid(x, y);
    if (dio.has(k)) return;
    dio.add(k);
    water.push([x, y]);
  };
  for (const b of blocks) {
    const M = TUNE.margin;
    for (let y = b.y - M; y < b.y + b.h + M; y++) for (let x = b.x - M; x < b.x + b.w + M; x++) addWater(x, y);
    mx0 = Math.min(mx0, b.x - M); my0 = Math.min(my0, b.y - M); mx1 = Math.max(mx1, b.x + b.w + M - 1); my1 = Math.max(my1, b.y + b.h + M - 1);
  }
  if (plot) {
    const M = TUNE.margin;
    for (let y = -PLOT - M; y <= PLOT + M; y++) for (let x = -PLOT - M; x <= PLOT + M; x++) addWater(x, y);
  }
  if (blocks.length) {
    const seen = new Set<number>();
    const stack: [number, number][] = [[mx0 - 1, my0 - 1]];
    while (stack.length) {
      const [x, y] = stack.pop()!;
      if (x < mx0 - 1 || x > mx1 + 1 || y < my0 - 1 || y > my1 + 1) continue;
      const k = cid(x, y);
      if (seen.has(k) || dio.has(k)) continue;
      seen.add(k);
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    for (let y = my0; y <= my1; y++) for (let x = mx0; x <= mx1; x++) if (!seen.has(cid(x, y))) addWater(x, y);
  }
  // Bridges for shared words; a lantern string when the two islands are not canal neighbours.
  const bridges: Bridge[] = [], garlands: Garland[] = [];
  const used = new Set<number>();
  const roads = [...town.roads]
    .filter((r) => r.shared.length && byDistrict.has(r.a) && byDistrict.has(r.b))
    .sort((p, q) => q.shared.length - p.shared.length || byDistrict.get(p.b)!.d.order - byDistrict.get(q.b)!.d.order);
  for (const r of roads) {
    const a = byDistrict.get(r.a)!, b = byDistrict.get(r.b)!;
    const tiles = r.shared.map((k) => town.byKey.get(k)).filter((t): t is Tile => !!t);
    const lit = tiles.some((t) => t.lit);
    const anchor = byKey.get(r.shared[0]);
    const c = crossing(a, b, anchor, land, used);
    if (!c) { garlands.push({ from: anchor ?? null, a, b, shared: r.shared, lit }); continue; }
    for (let s = c.from; s <= c.to; s++) used.add(c.axis === 0 ? cid(s, c.row) : cid(c.row, s));
    const width = r.shared.length >= 4 ? 0.5 : r.shared.length >= 2 ? 0.38 : 0.26;
    const br: Bridge = { a, b, axis: c.axis, row: c.row, from: c.from, to: c.to, shared: r.shared, width, stage: Math.max(0, ...tiles.map((t) => t.stage)), material: materialOf(tiles, r.a, r.b), lit };
    bridges.push(br);
    const [ex, ey] = c.axis === 0 ? [c.to, c.row] : [c.row, c.to];
    items.push({ depth: (ex + ey) * 2 + 1, k: 1, bridge: br });
  }
  // The props (v1 ticket 40): the signpost, its lantern and the stone, in the middle of the empty land or at the
  // oldest district's corner, off its land and clear of the bridges.
  let props: PropsSpot | undefined;
  if (town.snapshot.props) {
    if (plot) props = propsSpot(0, 0, null);
    else if (blocks.length) {
      const at = propsCorner(blocks[0], land, used);
      props = propsSpot(at.cell[0], at.cell[1], at.toward);
      addWater(at.cell[0], at.cell[1]);
    }
    if (props) items.push({ depth: (props.cell[0] + props.cell[1]) * 2 + 0.5, k: 3, props });
  }
  const sideL: [number, number, number][] = [], sideR: [number, number, number][] = [];
  for (const k of dio) {
    const x = Math.floor(k / 8192) - 4096, y = (k % 8192) - 4096, z = ground.has(k) ? 0 : SEA;
    if (!dio.has(cid(x, y + 1))) sideL.push([x, y, z]);
    if (!dio.has(cid(x + 1, y))) sideR.push([x, y, z]);
  }

  items.sort((p, q) => p.depth - q.depth);

  // Light: glows on the ground, reflections in the water in front of lit lots, lamps on lit bridges.
  const glows: [number, number, number][] = [], reflections: [number, number][] = [];
  const reflect = (x: number, y: number) => reflections.push([sx(x + 0.5, y + 0.5), sy(x + 0.5, y + 0.5, SEA)]);
  for (const l of lots) {
    if (!l.t.lit) continue;
    glows.push([sx(l.gx + 0.5, l.gy + 0.5), sy(l.gx + 0.5, l.gy + 0.5), HW * (l.t.stage >= 3 ? 1.9 : 1.25)]);
    if (!land.has(cid(l.gx + 1, l.gy))) reflect(l.gx + 1, l.gy);
    if (!land.has(cid(l.gx, l.gy + 1))) reflect(l.gx, l.gy + 1);
  }
  for (const br of bridges) {
    if (!br.lit) continue;
    for (const s of [br.from, br.to]) {
      const [x, y] = br.axis === 0 ? [s, br.row] : [br.row, s];
      glows.push([sx(x + 0.5, y + 0.5), sy(x + 0.5, y + 0.5), HW * 1.1]);
      reflect(x, y);
    }
  }
  if (props) {
    const [px, py] = props.post;
    glows.push([sx(px, py), sy(px, py), HW * 1.1]);
    if (props.islet) reflections.push([sx(px + 0.3, py + 0.3), sy(px + 0.3, py + 0.3, SEA)]);
  }
  const ripples: [number, number, number][] = [];
  for (const [x, y] of water) {
    const hv = hash(`tb-ripple|${x}|${y}`);
    if (hv % 5) continue;
    ripples.push([sx(x + 0.5, y + 0.5) + ((hv >>> 8) % 13) - 6, sy(x + 0.5, y + 0.5, SEA) + ((hv >>> 16) % 7) - 3, 3 + ((hv >>> 20) % 5)]);
  }

  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const k of dio) {
    const x = Math.floor(k / 8192) - 4096, y = (k % 8192) - 4096;
    x0 = Math.min(x0, sx(x, y + 1)); x1 = Math.max(x1, sx(x + 1, y));
    y0 = Math.min(y0, sy(x, y, 0)); y1 = Math.max(y1, sy(x + 1, y + 1, -SLAB));
  }
  for (const l of lots) y0 = Math.min(y0, sy(l.gx + 0.5, l.gy + 0.5, l.top + 8));
  if (props) y0 = Math.min(y0, sy(props.post[0], props.post[1], LANTERN_TOP + 8));
  if (!Number.isFinite(x0)) { x0 = -HW; x1 = HW; y0 = -HH; y1 = HH; }

  const attempts = town.snapshot.answers ?? town.tiles.reduce((s, t) => s + t.item.attempts, 0);
  return {
    blocks, byDistrict, lots, byKey, land, water, sideL, sideR, items, bridges, garlands, glows, reflections, ripples,
    ...(plot ? { plot } : {}), ...(props ? { props } : {}),
    bounds: { x0, y0, x1, y1 },
    stars: Math.min(420, Math.round(10 + 11 * Math.sqrt(attempts))),
    moon: town.stats.practised ? town.stats.lit / town.stats.practised : 0,
    seed: town.snapshot.id.replace(/\+.*$/, ''),
  };
}

// ---- bridges, as both looks see them ----------------------------------------------------------------------

/**
 * A bridge's profile in world px, as B draws it (d-road.ts's span follows it): its deck's centre line `cc` and
 * half width `hw` across the span, its height `z(i)` at step i of N, and `at(i, across, z)` in world px.
 */
export function bridgeGeo(br: Bridge) {
  const kind = br.material;
  const a0 = br.from - 0.14, a1 = br.to + 1.14, span = a1 - a0, cc = br.row + 0.5;
  const hw = (br.width / 2) * (kind === 0 ? 0.75 : 1);
  const rise = kind === 2 ? 2 + span * 1.3 : kind === 1 ? 1.2 : -1.3;
  const N = 10;
  const z = (i: number) => 0.7 + rise * Math.sin((Math.PI * i) / N);
  const at = (i: number, c: number, zz: number): [number, number] => {
    const s = a0 + (span * i) / N;
    return br.axis === 0 ? [sx(s, c), sy(s, c, zz)] : [sx(c, s), sy(c, s, zz)];
  };
  return { kind, cc, hw, N, z, at };
}

const bridgeBoxes = new WeakMap<Bridge, Box>();
/** A bridge's screen box in world px (its deck and rails; lamps reach 10 px higher). */
export function bridgeBox(br: Bridge): Box {
  const had = bridgeBoxes.get(br);
  if (had) return had;
  const { N, z, at, cc, hw } = bridgeGeo(br);
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const i of [0, N / 2, N]) for (const e of [cc - hw, cc + hw]) for (const zz of [z(i) - 1, z(i) + 3]) {
    const [x, y] = at(i, e, zz);
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  const b: Box = [x0, y0, x1, y1];
  bridgeBoxes.set(br, b);
  return b;
}

/** A bridge's outline in world px: its far rail's top along the span, then its near edge under the deck back. */
export function bridgeOutline(br: Bridge): [number, number][] {
  const { N, z, at, cc, hw } = bridgeGeo(br);
  const out: [number, number][] = [];
  for (let i = 0; i <= N; i++) out.push(at(i, cc - hw, z(i) + 3));
  for (let i = N; i >= 0; i--) out.push(at(i, cc + hw, z(i) - 1));
  return out;
}

/** Whether world point (x, y) is on the bridge, or within `pad` world px of it (a narrow deck is hard to tap). */
export function bridgeHit(br: Bridge, x: number, y: number, pad: number): boolean {
  const [x0, y0, x1, y1] = bridgeBox(br);
  if (x < x0 - pad || x > x1 + pad || y < y0 - pad || y > y1 + pad) return false;
  const p = bridgeOutline(br);
  let inside = false, near = Infinity;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) {
    const [ax, ay] = p[i], [bx, by] = p[j];
    if ((ay > y) !== (by > y) && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
    const dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy;
    const t = L ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / L)) : 0;
    near = Math.min(near, Math.hypot(x - ax - t * dx, y - ay - t * dy));
  }
  return inside || near <= pad;
}
